// screens/accounts/BecomeAgent.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../providers/AuthProvider';
import { useToast } from '../providers/ToastProvider';
import {
  Store,
  Globe,
  Building2,
  User,
  Phone,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Loader2,
  Coins,
  Shield,
  TrendingUp,
  Users,
  ChevronLeft,
  Sparkles,
} from 'lucide-react';

/* ══════════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════════ */

const BecomeAgent = ({ mode = 'upgrade' }) => {
  const { colors } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    becomeAgent,
    initiateRegistrationPayment,
    pollRegistrationStatus,
    refreshUser,
    clearUnpaidGate,
  } = useAuth();

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    shop_url: '',
    shop_name: '',
    shop_description: '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone_number: user?.phone_number || '',
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* ── Guard: must be logged in ────────────────────────── */
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  /* ── Validate shop_url client-side (mirror of backend) ─ */
  const validateShopUrl = (url) => {
    if (!url) return 'Store URL is required';
    const t = url.trim();
    if (t.length < 3) return 'At least 3 characters';
    if (t.length > 30) return 'At most 30 characters';
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(t)) {
      return 'Only lowercase letters, numbers, hyphens';
    }
    if (t.includes('--')) return 'No consecutive hyphens';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'shop_url') {
      const cleaned = value
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-');
      setForm((p) => ({ ...p, shop_url: cleaned }));
      if (errors.shop_url) setErrors((p) => ({ ...p, shop_url: '' }));
      return;
    }
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  /* ── Upgrade flow (mode=upgrade) ─────────────────────── */
  const handleUpgrade = async (e) => {
    e.preventDefault();
    setErrors({});

    const urlError = validateShopUrl(form.shop_url);
    if (urlError) {
      setErrors({ shop_url: urlError });
      showToast(urlError, 'error');
      return;
    }

    setBusy(true);
    try {
      const payload = {
        shop_url: form.shop_url.trim(),
      };
      if (form.shop_name.trim()) payload.shop_name = form.shop_name.trim();
      if (form.shop_description.trim()) payload.shop_description = form.shop_description.trim();
      if (form.first_name.trim()) payload.first_name = form.first_name.trim();
      if (form.last_name.trim()) payload.last_name = form.last_name.trim();
      if (form.phone_number.trim()) payload.phone_number = form.phone_number.trim();

      const res = await becomeAgent(payload);

      if (!res.ok) {
        if (res.errors) {
          setErrors(res.errors);
          const first = Object.values(res.errors)[0];
          showToast(Array.isArray(first) ? first[0] : first, 'error');
        } else {
          showToast(res.error || 'Upgrade failed.', 'error');
        }
        setBusy(false);
        return;
      }

      const reg = res.data.registration || {};

      // Fee zero — auto-activated
      if (!reg.requires_fee) {
        await refreshUser();
        showToast('You are now an agent!', 'success');
        navigate('/');
        return;
      }

      // Fee > 0 — redirect to Paystack
      if (reg.authorization_url) {
        if (reg.callback_url) {
          localStorage.setItem('registration_dashboard_url', reg.callback_url);
        }
        localStorage.setItem('registration_user_id', res.data.user_id);
        setRedirecting(true);
        showToast('Redirecting to payment…', 'info');
        setTimeout(() => {
          window.location.href = reg.authorization_url;
        }, 800);
        return;
      }

      // Fallback
      showToast('Account upgraded. Please complete payment from your dashboard.', 'info');
      navigate('/');
    } catch (e) {
      showToast('Network error. Please try again.', 'error');
      setBusy(false);
    }
  };

  /* ── Payment flow (mode=payment) ─────────────────────── */
  const handlePayment = async () => {
    setBusy(true);
    try {
      const res = await initiateRegistrationPayment();
      if (!res.ok) {
        showToast(res.error || 'Could not start payment.', 'error');
        setBusy(false);
        return;
      }

      const d = res.data || {};

      if (d.zero_fee) {
        await refreshUser();
        clearUnpaidGate();
        showToast('Registration complete.', 'success');
        navigate('/');
        return;
      }

      if (d.already_paid) {
        showToast('Payment confirmed. Finishing setup…', 'info');
        const poll = await pollRegistrationStatus(user?.id);
        if (poll.ok) {
          clearUnpaidGate();
          showToast('Registration complete.', 'success');
          navigate('/');
        } else {
          showToast(
            poll.error || 'Still confirming. Please check back shortly.',
            'warning'
          );
        }
        setBusy(false);
        return;
      }

      if (d.authorization_url) {
        if (d.callback_url) {
          localStorage.setItem('registration_dashboard_url', d.callback_url);
        }
        setRedirecting(true);
        setTimeout(() => {
          window.location.href = d.authorization_url;
        }, 500);
        return;
      }

      showToast('Unexpected response. Please try again.', 'error');
      setBusy(false);
    } catch (e) {
      showToast('Network error. Please try again.', 'error');
      setBusy(false);
    }
  };

  /* ══════════════════════════════════════════════════════
     Render — shared
     ══════════════════════════════════════════════════════ */

  const isPaymentMode = mode === 'payment';

  const shell = (inner) => (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: colors.mainBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '20px 16px' : '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-200px',
          right: '-200px',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colors.btnBg}08, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-250px',
          left: '-250px',
          width: '700px',
          height: '700px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colors.btnBg}05, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          width: '100%',
          maxWidth: isMobile ? '500px' : '1000px',
          backgroundColor: colors.raisedBg,
          borderRadius: isMobile ? 16 : 24,
          boxShadow: `0 20px 60px ${colors.shadow}`,
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: '1fr 1.2fr',
        }}
      >
        {inner}
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════
     Payment mode
     ══════════════════════════════════════════════════════ */
  if (isPaymentMode) {
    return shell(
      <div style={{ padding: isMobile ? '28px 20px 24px' : '48px 40px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: colors.warningBg || '#FEF3C7',
              color: colors.warning || '#F59E0B',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Coins size={22} />
          </div>
          <div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: colors.heading,
              }}
            >
              Complete registration
            </div>
            <div
              style={{
                fontSize: 13,
                color: colors.secondaryText,
                marginTop: 2,
              }}
            >
              Finish your payment to unlock agent tools.
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '14px 16px',
            borderRadius: 12,
            background: colors.subtleBg || colors.hoverBg,
            border: `1px solid ${colors.border}`,
            marginBottom: 20,
            fontSize: 13,
            lineHeight: 1.55,
            color: colors.text,
          }}
        >
          Your account can browse the platform while unpaid, but write
          actions (creating orders, adding items to cart, enrolling in
          courses, withdrawals, etc.) are locked until the one-time
          registration fee is paid.
        </div>

        <button
          type="button"
          onClick={handlePayment}
          disabled={busy || redirecting}
          style={{
            width: '100%',
            padding: '15px',
            borderRadius: 12,
            border: 'none',
            background: colors.btnBg,
            color: colors.btnText,
            fontSize: 16,
            fontWeight: 700,
            cursor: busy || redirecting ? 'not-allowed' : 'pointer',
            opacity: busy || redirecting ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontFamily: 'inherit',
          }}
        >
          {busy || redirecting ? (
            <>
              <Loader2
                size={18}
                style={{ animation: 'spin 1s linear infinite' }}
              />
              {redirecting ? 'Redirecting…' : 'Processing…'}
            </>
          ) : (
            <>
              Complete payment
              <ArrowRight size={18} />
            </>
          )}
        </button>

        <div
          style={{
            marginTop: 16,
            textAlign: 'center',
            fontSize: 12.5,
            color: colors.secondaryText,
          }}
        >
          <Link
            to="/"
            style={{
              color: colors.btnBg,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     Upgrade mode
     ══════════════════════════════════════════════════════ */

  const leftPanel = !isMobile && (
    <div
      style={{
        padding: '48px 40px',
        background: `linear-gradient(135deg, ${colors.btnBg}12, ${colors.btnBg}04)`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 28,
          }}
        >
          <span
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: colors.heading,
              letterSpacing: '-0.5px',
            }}
          >
            Azoemart
          </span>
          <span
            style={{
              fontSize: 10,
              padding: '3px 10px',
              borderRadius: 20,
              background: colors.btnBg,
              color: colors.btnText,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            Beta
          </span>
        </div>

        <h1
          style={{
            fontSize: 24,
            fontWeight: 800,
            color: colors.heading,
            margin: '0 0 8px',
            letterSpacing: '-0.02em',
          }}
        >
          Become an Agent
        </h1>
        <p
          style={{
            fontSize: 14,
            color: colors.secondaryText,
            margin: '0 0 24px',
            lineHeight: 1.6,
          }}
        >
          Turn your account into your own storefront on Azoemart. Sell
          data packages, build a sub-agent network, and track your
          profits — all from one dashboard.
        </p>

        <div style={{ display: 'grid', gap: 10 }}>
          {[
            { icon: TrendingUp, text: 'Set your own prices' },
            { icon: Users, text: 'Recruit and manage sub-agents' },
            { icon: Shield, text: 'Track commissions & payouts' },
            { icon: Globe, text: 'Your own custom store URL' },
          ].map(({ icon: Icon, text }) => (
            <div
              key={text}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 10,
                background: `${colors.btnBg}08`,
                fontSize: 13.5,
                color: colors.text,
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: `${colors.btnBg}20`,
                  color: colors.btnBg,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon size={14} />
              </span>
              {text}
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          marginTop: 24,
          fontSize: 12.5,
          color: colors.secondaryText,
        }}
      >
        Already an agent?{' '}
        <Link
          to="/"
          style={{
            color: colors.btnBg,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );

  const rightPanel = (
    <div style={{ padding: isMobile ? '28px 20px 24px' : '48px 40px' }}>
      {isMobile && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'transparent',
            border: 'none',
            color: colors.secondaryText,
            cursor: 'pointer',
            padding: '4px 0',
            marginBottom: 12,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          <ChevronLeft size={16} /> Back
        </button>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 6,
        }}
      >
        <Store size={22} color={colors.btnBg} />
        <h2
          style={{
            margin: 0,
            fontSize: isMobile ? 20 : 22,
            fontWeight: 800,
            color: colors.heading,
            letterSpacing: '-0.02em',
          }}
        >
          Set up your store
        </h2>
      </div>
      <p
        style={{
          margin: '0 0 20px',
          fontSize: 13,
          color: colors.secondaryText,
        }}
      >
        Pick a store URL and we'll provision your storefront instantly.
      </p>

      <form onSubmit={handleUpgrade}>
        {/* Store URL */}
        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: colors.text,
              display: 'block',
              marginBottom: 4,
            }}
          >
            Store URL <span style={{ color: colors.error }}>*</span>
          </label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              background: colors.raisedBg,
              borderRadius: 10,
              border: `1.5px solid ${
                errors.shop_url ? colors.error : colors.border
              }`,
              padding: '0 12px',
              minHeight: 44,
            }}
          >
            <span
              style={{
                fontSize: 12.5,
                color: colors.secondaryText,
                whiteSpace: 'nowrap',
                opacity: 0.7,
              }}
            >
              https://
            </span>
            <input
              type="text"
              name="shop_url"
              value={form.shop_url}
              onChange={handleChange}
              placeholder="my-shop"
              style={{
                flex: 1,
                padding: '10px 4px',
                border: 'none',
                background: 'transparent',
                color: colors.text,
                fontSize: 14,
                outline: 'none',
                minWidth: 60,
                fontFamily: 'inherit',
              }}
            />
            <span
              style={{
                fontSize: 12.5,
                color: colors.secondaryText,
                whiteSpace: 'nowrap',
                opacity: 0.7,
              }}
            >
              .azoeshop.store
            </span>
          </div>
          {errors.shop_url ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 4,
                fontSize: 12,
                color: colors.error,
              }}
            >
              <AlertCircle size={13} />
              {Array.isArray(errors.shop_url)
                ? errors.shop_url[0]
                : errors.shop_url}
            </div>
          ) : (
            form.shop_url &&
            !validateShopUrl(form.shop_url) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginTop: 4,
                  fontSize: 12,
                  color: colors.success || '#10B981',
                }}
              >
                <CheckCircle size={13} />
                https://{form.shop_url}.azoeshop.store
              </div>
            )
          )}
        </div>

        {/* Shop name */}
        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: colors.text,
              display: 'block',
              marginBottom: 4,
            }}
          >
            Shop name
          </label>
          <div style={{ position: 'relative' }}>
            <Building2
              size={16}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.secondaryText,
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              name="shop_name"
              value={form.shop_name}
              onChange={handleChange}
              placeholder="My Store"
              style={{
                width: '100%',
                padding: '11px 12px 11px 38px',
                borderRadius: 10,
                border: `1.5px solid ${colors.border}`,
                background: colors.raisedBg,
                color: colors.text,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: colors.text,
              display: 'block',
              marginBottom: 4,
            }}
          >
            Short description
          </label>
          <input
            type="text"
            name="shop_description"
            value={form.shop_description}
            onChange={handleChange}
            placeholder="Tell customers about your store"
            style={{
              width: '100%',
              padding: '11px 12px',
              borderRadius: 10,
              border: `1.5px solid ${colors.border}`,
              background: colors.raisedBg,
              color: colors.text,
              fontSize: 14,
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* First + Last name */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            marginBottom: 14,
          }}
        >
          <div>
            <label
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.text,
                display: 'block',
                marginBottom: 4,
              }}
            >
              First name
            </label>
            <div style={{ position: 'relative' }}>
              <User
                size={15}
                style={{
                  position: 'absolute',
                  left: 11,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.secondaryText,
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                placeholder="Kwame"
                style={{
                  width: '100%',
                  padding: '10px 10px 10px 34px',
                  borderRadius: 10,
                  border: `1.5px solid ${colors.border}`,
                  background: colors.raisedBg,
                  color: colors.text,
                  fontSize: 13.5,
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.text,
                display: 'block',
                marginBottom: 4,
              }}
            >
              Last name
            </label>
            <input
              type="text"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              placeholder="Mensah"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: `1.5px solid ${colors.border}`,
                background: colors.raisedBg,
                color: colors.text,
                fontSize: 13.5,
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        {/* Phone */}
        <div style={{ marginBottom: 18 }}>
          <label
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: colors.text,
              display: 'block',
              marginBottom: 4,
            }}
          >
            Phone number
          </label>
          <div style={{ position: 'relative' }}>
            <Phone
              size={15}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.secondaryText,
                pointerEvents: 'none',
              }}
            />
            <input
              type="tel"
              name="phone_number"
              value={form.phone_number}
              onChange={handleChange}
              placeholder="+233201234567"
              style={{
                width: '100%',
                padding: '11px 12px 11px 38px',
                borderRadius: 10,
                border: `1.5px solid ${colors.border}`,
                background: colors.raisedBg,
                color: colors.text,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={busy || redirecting}
          style={{
            width: '100%',
            padding: '15px',
            borderRadius: 12,
            border: 'none',
            background: colors.btnBg,
            color: colors.btnText,
            fontSize: 15,
            fontWeight: 700,
            cursor: busy || redirecting ? 'not-allowed' : 'pointer',
            opacity: busy || redirecting ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontFamily: 'inherit',
          }}
        >
          {busy || redirecting ? (
            <>
              <Loader2
                size={18}
                style={{ animation: 'spin 1s linear infinite' }}
              />
              {redirecting ? 'Redirecting…' : 'Upgrading…'}
            </>
          ) : (
            <>
              <Sparkles size={16} />
              Become Agent
              <ArrowRight size={16} />
            </>
          )}
        </button>

        {isMobile && (
          <div
            style={{
              marginTop: 16,
              textAlign: 'center',
              fontSize: 12.5,
              color: colors.secondaryText,
            }}
          >
            Already an agent?{' '}
            <Link
              to="/"
              style={{
                color: colors.btnBg,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Dashboard
            </Link>
          </div>
        )}
      </form>
    </div>
  );

  return shell(
    <>
      {leftPanel}
      {rightPanel}
    </>
  );
};

/* Add keyframe style tag */
if (typeof document !== 'undefined' && !document.getElementById('become-agent-spin')) {
  const s = document.createElement('style');
  s.id = 'become-agent-spin';
  s.innerHTML = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

export default BecomeAgent;